import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatErr, imgSrc } from "@/lib/api";

const autosave = () => window.dispatchEvent(new Event("am:autosave"));

async function uploadAll(files, onProgress) {
  const paths = [];
  for (const [i, file] of files.entries()) {
    onProgress(i + 1);
    const fd = new FormData();
    fd.append("file", file);
    try { const { data } = await api.post("/upload", fd); paths.push(data.path); }
    catch (e) { toast.error(`${file.name}: ${formatErr(e)}`); }
  }
  return paths;
}

function placeImages(items, paths, tpl) {
  const queue = [...paths];
  const filled = items.map((it) => (!it.image && queue.length ? { ...it, image: queue.shift() } : it));
  return [...filled, ...queue.map((p) => ({ ...JSON.parse(JSON.stringify(tpl)), image: p }))];
}

export const MultiUpload = ({ value, onChange, tpl, testid = "gallery-multi-upload" }) => {
  const ref = useRef(null);
  const [prog, setProg] = useState(null);

  const pick = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    setProg({ done: 0, total: files.length });
    const paths = await uploadAll(files, (n) => setProg({ done: n, total: files.length }));
    setProg(null);
    if (!paths.length) return;
    onChange(placeImages(value, paths, tpl));
    autosave();
    toast.success(`Dodano ${paths.length} zdj.`);
  };

  return (
    <>
      <input ref={ref} type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={pick} data-testid={`${testid}-input`} />
      <button type="button" className="mini" disabled={!!prog} onClick={() => ref.current.click()} data-testid={`${testid}-btn`}>
        {prog ? `Wgrywanie ${prog.done}/${prog.total}…` : "+ Wgraj kilka zdjęć naraz"}
      </button>
    </>
  );
};

const parse = (pos) => (pos || "50% 50%").split(" ").map((x) => parseFloat(x) || 50);

export const PositionField = ({ value, onChange, siblings, label, testid }) => {
  const [x, y] = parse(value);
  if (!siblings?.image) return null;
  const set = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const nx = Math.round(((e.clientX - r.left) / r.width) * 100);
    const ny = Math.round(((e.clientY - r.top) / r.height) * 100);
    onChange(`${Math.min(100, Math.max(0, nx))}% ${Math.min(100, Math.max(0, ny))}%`);
  };
  const src = imgSrc(siblings.image);
  return (
    <div className="fe-field" data-testid={testid}>
      <label>{label} – kliknij na zdjęciu najważniejsze miejsce</label>
      <div className="pos-wrap">
        <div className="pos-pick" onClick={set} data-testid={`${testid}-picker`}>
          <img src={src} alt="" draggable={false} />
          <span className="pos-dot" style={{ left: `${x}%`, top: `${y}%` }} />
        </div>
        <div className="pos-previews">
          <span className="label">Podgląd kafelka</span>
          <div className={`pos-tile${siblings.tall ? " tall" : ""}`}><img src={src} alt="" style={{ objectPosition: `${x}% ${y}%` }} data-testid={`${testid}-preview`} /></div>
          <button type="button" className="mini" onClick={() => onChange("50% 50%")}>Wyśrodkuj</button>
        </div>
      </div>
    </div>
  );
};

export const PhotoList = ({ value, onChange, label, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = [...(e.target.files || [])].slice(0, 3 - value.length);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    const paths = await uploadAll(files, () => {});
    setBusy(false);
    if (paths.length) { onChange([...value, ...paths]); autosave(); }
  };
  return (
    <div className="fe-field" data-testid={testid}>
      <label>{label}</label>
      <div className="photo-list">
        {value.map((p, i) => (
          <div className="photo-thumb" key={p}>
            <img src={imgSrc(p)} alt="" />
            <button type="button" className="mini danger" onClick={() => { onChange(value.filter((_, j) => j !== i)); autosave(); }} data-testid={`${testid}-remove-${i}`}>Usuń</button>
          </div>
        ))}
        {value.length < 3 && (
          <>
            <input ref={ref} type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={pick} data-testid={`${testid}-input`} />
            <button type="button" className="mini" disabled={busy} onClick={() => ref.current.click()} data-testid={`${testid}-add-btn`}>{busy ? "Wgrywanie…" : "+ Dodaj zdjęcie"}</button>
          </>
        )}
      </div>
    </div>
  );
};
